<!--
  @component
  Creates an interaction layer (in HTML) using [d3-quadtree](https://github.com/d3/d3-quadtree) to find the nearest datapoint to the mouse. This component creates a snippet that exposes variables `x`, `y`, `found` (the found datapoint), `visible` (a Boolean whether any data was found) and `e` (the event object).

  The quadtree searches across both the x and y dimensions at the same time. But if you want to only search across one, set the `x` and `y` props to the same value. For example, the [shared tooltip component](https://layercake.graphics/components/SharedTooltip.html.svelte) sets `y='x'` since it's nicer behavior to only pick up on the nearest x-value.
 -->
<script>
  import { getContext } from "svelte";
  import { quadtree } from "d3-quadtree";
  import { active } from "d3";

  const { data, xGet, xScale, y, yGet, yScale, width, height, custom } = getContext("LayerCake");

  let visible = $state(false);
  let found = $state({});
  let activeHighlight = $state();
  let e = $state({});

  /**
   * @typedef {Object} Props
   * @property {string} [xAccessor='x'] - The dimension to search across when moving the mouse left and right.
   * @property {string} [yAccessor='y'] - The dimension to search across when moving the mouse up and down.
   * @property {number|undefined} [searchRadius] - The number of pixels to search around the mouse's location. This is the third argument passed to [`quadtree.find`](https://github.com/d3/d3-quadtree#quadtree_find) and by default a value of `undefined` means an unlimited range.
   * @property {Array<Object>|undefined} [dataset] - The dataset to work off of—defaults to $data if left unset. You can pass override the default here in here in case you don't want to use the main data or it's in a strange format.
   * @property {import('svelte').Snippet<[any]>} [children]
   */

  /** @type {Props} */
  let {
    xAccessor = "x",
    yAccessor = "x",
    searchRadius,
    xKey = "year",
    dataset,
    children
  } = $props();

  let xGetter = $derived(xAccessor === "x" ? myXGet : $yGet);
  let yGetter = $derived(yAccessor === "y" ? $yGet : myXGet);

  function myXGet(d) {
    return $xScale(d[xKey]);
  }

  /** @param {MouseEvent} evt */
  function findItem(evt) {
    e = evt;
    const xLayerKey = /** @type {'layerX'|'layerY'} */ (`layer${xAccessor.toUpperCase()}`);
    const yLayerKey = /** @type {'layerX'|'layerY'}*/ (`layer${yAccessor.toUpperCase()}`);

    found = finder.find(evt[xLayerKey], evt[yLayerKey], searchRadius) || {};

    if (!found.hasOwnProperty(xKey)) {
      setInActive();
      $custom.onhover(null);
      return;
    }

    const mouseYValue = $yScale.invert(evt.layerY);

    let cumulative = 0;
    let segmentValue = 0;
    let foundProgram = null;
    for (let programVariable of $custom.seriesVariables) {
      segmentValue = Number(found[programVariable] ?? 0); //in case that segment isn't present that year
      if (mouseYValue >= cumulative && mouseYValue <= cumulative + segmentValue) {
        foundProgram = programVariable;
        break;
      }
      cumulative += segmentValue;
    }
    if (foundProgram == null) {
      setInActive();
      $custom.onhover(null);
      return;
    }
    activeHighlight = {
      x: xGetter(found), //evt.layerX,
      y: evt.layerY,
      line_y_top: $yScale(cumulative + segmentValue),
      line_y_bottom: $yScale(cumulative),

      key: foundProgram,
      year: found[xKey],
      value: found[foundProgram] ?? 0
    };
    visible = true;
    $custom.onhover(foundProgram);
  }

  let finder = $derived(
    quadtree()
      .extent([
        [-1, -1],
        [$width + 1, $height + 1]
      ])
      .x(xGetter)
      .y(yGetter)
      .addAll(dataset || $data)
  );

  function setInActive() {
    visible = false;
    activeHighlight = undefined;
    $custom.onhover(null);
  }

  function handleKeydown(event, d) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      $custom.onclick(activeHighlight?.key ?? null);
    }
  }
</script>

<div
  class="bg"
  onmousemove={findItem}
  onclick={() => $custom.onclick(activeHighlight?.key ?? null)}
  onkeydown={(event) => handleKeydown(event, d)}
  onmouseout={() => setInActive()}
  onblur={() => setInActive()}
  role="tooltip"
></div>
{@render children?.({
  x: activeHighlight?.x || 0,
  y: activeHighlight?.y || 0,
  line_y_top: activeHighlight?.line_y_top || 0,
  line_y_bottom: activeHighlight?.line_y_bottom || 0,

  year: activeHighlight?.year || 0,
  key: activeHighlight?.key || 0,
  value: activeHighlight?.value || 0,
  activeHighlight,
  visible,
  e
})}

<style>
  .bg {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
</style>
